/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from 'react';
import styled from 'styled-components';
import ModalPortal from 'components/Modal/ModalPortal';
import RomModalLayout from 'components/Modal/RomModal/RomModalLayout';
import useHorizontalBarStyle from 'hooks/useHorizontalBarStyle';
import { chartColor, color, titleColor } from 'asserts/styles/colors';
import { commonTooltipOptions } from 'consts/chartOptions';
import { CSSTransition } from 'react-transition-group';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import useAddDataInBarChart from 'hooks/useAddDataInBarChart';
import { romLabels } from 'consts/constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface RomStatisticsChartProps {
  chartData: RomData;
}

const RomStatisticsBarChart = ({ chartData }: RomStatisticsChartProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  const [isOPenModal, setIsOpenModal] = useState<boolean>(false);
  const [titleText, setTitleText] = useState<string>('');
  const [selectedChartType, setSelectedChartType] = useState<string | null>(
    null
  );

  const chartType = ['lumbarFlexion', 'lumbarExtension'];

  const lumbarFlexion = chartData?.lumbarFlexion || [];
  const lumbarExtension = chartData?.lumbarExtension || [];

  const lumbarFlexionData = [lumbarFlexion.caution, lumbarFlexion.normal];

  const lumbarExtensionData = [lumbarExtension.caution, lumbarExtension.normal];

  const romTypes = [
    { title: '허리 굴곡', data: lumbarFlexionData, total: lumbarFlexion.total },
    {
      title: '허리 신전',
      data: lumbarExtensionData,
      total: lumbarExtension.total
    }
  ];

  const dynamicOptions = romTypes.map((rom) => ({
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: { legend: { display: false }, tooltip: commonTooltipOptions },
    scales: {
      x: {
        display: false,
        ticks: {
          color: `${color.white}`,
          font: {
            family: 'Noto Sans KR'
          }
        },
        max: rom.total
      },
      y: { display: false, ticks: { color: `${color.white}` } }
    }
  }));

  const handleClickOpenModal = (index: number) => {
    const selectedChartType = chartType[index];

    setIsOpenModal(true);
    setSelectedChartType(selectedChartType);
  };

  const handleClickCloseModal = () => {
    setIsOpenModal(false);
  };

  return (
    <>
      {romTypes.map((rom, index) => (
        <RomChart
          data={rom.data}
          title={rom.title}
          key={index}
          options={dynamicOptions[index]}
          labels={romLabels}
          setTitleText={setTitleText}
          onClickModal={() => handleClickOpenModal(index)}
        />
      ))}
      <CSSTransition
        in={isOPenModal}
        classNames="modal"
        nodeRef={nodeRef}
        timeout={300}
        unmountOnExit
      >
        <ModalPortal>
          <RomModalLayout
            labels={romLabels}
            titleText={titleText}
            selectedChartType={selectedChartType}
            onClickModal={handleClickCloseModal}
            isTwoChart={false}
          />
        </ModalPortal>
      </CSSTransition>
    </>
  );
};

const RomChart = ({
  title,
  options,
  data: chartData,
  labels,
  onClickModal,
  setTitleText
}: any) => {
  const { graphYellow, graphBlue } = chartColor;

  const data = {
    labels,
    datasets: [
      {
        data: chartData,
        backgroundColor: [graphYellow, graphBlue],
        barThickness: 8,
        borderRadius: 3
      }
    ]
  };

  const horizontalBarTrack = {
    id: 'horizontalBarTrack',
    beforeDatasetDraw: useHorizontalBarStyle()
  };

  const addDataInBarChart = {
    id: 'addDataInBarChart',
    beforeDatasetsDraw: useAddDataInBarChart('rom')
  };

  const handleClickChart = () => {
    setTitleText(title);
    onClickModal();
  };

  const reverseLabel = [...labels].reverse();

  return (
    <Wrapper onClick={handleClickChart}>
      <TitleWrapper>
        <PositionTitle>{title}</PositionTitle>
      </TitleWrapper>
      <ChartWrapper>
        <LabelWrapper>
          {reverseLabel.map((label: string) => (
            <div key={label}>{label}</div>
          ))}
        </LabelWrapper>
        <Bar
          data={data}
          options={options}
          plugins={[horizontalBarTrack, addDataInBarChart]}
        />
      </ChartWrapper>
    </Wrapper>
  );
};

export default RomStatisticsBarChart;

const Wrapper = styled.div`
  position: relative;
  width: 600px;
  height: 232px;
  margin-bottom: 16px;
  background: ${color.white005};
  border-radius: 16px;
  cursor: pointer;

  transition: background-color 0.25s ease-in-out;

  &:hover {
    background: ${titleColor.lightEmeraldBackGround};
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  padding: 24px;
`;

const PositionTitle = styled.p`
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.3px;
  color: ${chartColor.graphMobile};
`;

const ChartWrapper = styled.div`
  position: absolute;
  top: 72px;
  left: 50%;
  transform: translateX(-50%);
  width: 440px;
  height: 88px;
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: absolute;
  top: 12px;
  left: -39px;
  height: 63px;
  font-size: 13px;
  font-family: sans-serif;
`;
